import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import { TypedMemo } from '../../../../common/types';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { usePostFileMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { themeMessages } from './themeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface KitLogoFieldProps {
  accountId: number;
  /** The relative file url of the kit's logo, else null. */
  logoUrl: string | null;
  disabled: boolean;
  /** A new logo file id and its file url, or null twice to remove the logo. */
  onChange: (logoFileId: number | null, logoUrl: string | null) => void;
}

/**
 * The optional logo of the song (design q2 "Your logo"). The upload is the account file endpoint
 * the rest of the app uses; the renderer reads the file through the public-url guard.
 */
const KitLogoFieldComponent: React.FC<KitLogoFieldProps> = ({ accountId, logoUrl, disabled, onChange }) => {
  const intl = useIntl();
  const [postFile, { isLoading }] = usePostFileMutation();

  const onDrop = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (file === undefined) {
        return;
      }
      const formData = new FormData();
      formData.append('file', file, file.name);
      postFile({ accountId, formData })
        .then((result) => {
          if (isSuccessfulResult(result)) {
            onChange(result.data.id, result.data.url);
          }
        })
        .catch(console.error);
    },
    [accountId, onChange, postFile]
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: { 'image/png': ['.png'] },
    disabled: disabled || isLoading,
    noClick: true,
    noKeyboard: true,
  });
  const remove = useCallback(() => onChange(null, null), [onChange]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- react-dropzone hands its listeners over as a props bag
    <div className={styles.row} {...getRootProps()}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- same props bag, for the hidden file input */}
      <input {...getInputProps()} data-testid="kit-logo-input" />
      <span className={styles.logo}>
        {logoUrl === null ? (
          intl.formatMessage(themeMessages.logoPlaceholder)
        ) : (
          <img className={styles.logoImage} src={getFullAPIFileURL(logoUrl)} alt="" />
        )}
      </span>
      <div className={styles.grow}>
        <b className={styles.rowTitle}>{intl.formatMessage(themeMessages.logoTitle)}</b>
        <small className={styles.rowHint}>{intl.formatMessage(themeMessages.logoHint)}</small>
      </div>
      <SMLButton disabled={disabled || isLoading} onClick={open}>
        {intl.formatMessage(logoUrl === null ? themeMessages.logoAdd : themeMessages.logoReplace)}
      </SMLButton>
      {logoUrl !== null && (
        <SMLButton disabled={disabled} onClick={remove}>
          {intl.formatMessage(themeMessages.logoRemove)}
        </SMLButton>
      )}
    </div>
  );
};

export const KitLogoField = TypedMemo(KitLogoFieldComponent);
